import PropTypes from 'prop-types';
import styled from '@emotion/styled';
import { getImage } from '@utils/imageHelper';
import { Fonts, mediaQuery } from '@styles/index';

export const TEST_SUBSCRIPTION_BADGE = {
  ICONIC: 'iconic',
  PLUS: 'plus',
  PRIME: 'prime'
};

export const TEST_LISTING_CARD_VARIANT = {
  QUIZ: 'quiz',
  TEST_SERIES: 'test_series'
};

const CONTENT_GRADIENT = `linear-gradient(
  197deg,
  rgba(167, 195, 235, 0.3) -41.92%,
  rgba(255, 255, 255, 0.3) 61.52%
)`;

const SUBSCRIPTION_BADGE_CONFIG = {
  [TEST_SUBSCRIPTION_BADGE.ICONIC]: {
    label: 'Iconic',
    borderColor: '#ffb651',
    textColor: '#ffb651',
    icon: 'test-series-listing/cards/iconic-badge-icon.svg',
    iconSize: 12,
    isPrime: false
  },
  [TEST_SUBSCRIPTION_BADGE.PLUS]: {
    label: 'Plus',
    borderColor: '#27b772',
    textColor: '#08bd80',
    icon: 'test-series-listing/cards/plus-badge-icon.svg',
    iconSize: 13,
    isPrime: false
  },
  [TEST_SUBSCRIPTION_BADGE.PRIME]: {
    label: 'Prime',
    borderColor: '#7c51f7',
    textColor: '#7c51f7',
    icon: 'test-series-listing/cards/prime-badge-icon.svg',
    iconSize: 12,
    isPrime: true
  }
};

const Card = styled.article`
  box-sizing: border-box;
  display: flex;
  flex-direction: column;
  flex-shrink: 0;
  width: 273px;
  height: 209px;
  border-radius: 8px;
  background: #ffffff;
  cursor: ${({ $isClickable }) => ($isClickable ? 'pointer' : 'default')};
  overflow: hidden;

  ${({ $isLive, $isTestSeries }) => {
    if ($isTestSeries || $isLive) {
      return 'padding: 8px;';
    }

    return `
      gap: 24px;
      padding: 6px;
    `;
  }}

  ${mediaQuery.uptoMobile} {
    width: 100%;
    height: auto;
    min-height: 179px;
  }
`;

const ContentBlock = styled.div`
  display: flex;
  flex: 1;
  flex-direction: column;
  min-height: 0;
  overflow: hidden;
  border-radius: 4px;
  width: 100%;

  gap: 8px;
  padding: 12px 12px 16px;
  background: ${CONTENT_GRADIENT};
`;

const HeaderGroup = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
  width: 100%;
`;

const LiveRow = styled.div`
  display: flex;
  align-items: center;
  gap: 6px;
`;

const LiveIndicatorIcon = styled.img`
  flex-shrink: 0;
`;

const LivePill = styled.span`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  padding: 2px 6px;
  border-radius: 4px;
  background: #eb5757;
  font-size: 10px;
  line-height: 12px;
  font-weight: ${Fonts.BOLD};
  letter-spacing: 0.2px;
  text-transform: uppercase;
  color: #ffffff;
`;

const CategoryRow = styled.div`
  display: flex;
  align-items: center;
  gap: 6px;
  width: 100%;
`;

const LockBadge = styled.span`
  display: inline-flex;
  flex-shrink: 0;
  align-items: center;
  justify-content: center;
  width: 24px;
  height: 24px;
  border: 1px solid #f2f7fc;
  border-radius: 34px;
  background: #ffffff;
`;

const LockIcon = styled.img`
  flex-shrink: 0;
`;

const CategoryLabel = styled.span`
  font-size: 12px;
  line-height: 12px;
  font-weight: ${Fonts.BOLD};
  letter-spacing: 0.24px;
  text-transform: uppercase;
  color: #199aa2;
`;

const TextGroup = styled.div`
  display: flex;
  flex-direction: column;
  width: 100%;
  min-width: 0;

  ${({ $isLive, $isTestSeries }) => {
    if ($isTestSeries) {
      return 'gap: 8px;';
    }

    if ($isLive) {
      return 'gap: 8px; flex: 1; min-height: 0; overflow: hidden;';
    }

    return 'gap: 6px; flex: 1; min-height: 0; overflow: hidden;';
  }}
`;

const Title = styled.h3`
  margin: 0;
  font-size: 16px;
  line-height: 20px;
  font-weight: ${Fonts.SEMIBOLD};
  color: #3c4852;
  overflow: hidden;
  text-overflow: ellipsis;
  display: -webkit-box;
  -webkit-box-orient: vertical;
  -webkit-line-clamp: 2;
  word-break: break-word;

  ${({ $isTestSeries }) =>
    $isTestSeries
      ? `
    flex-shrink: 0;
    max-height: 40px;
  `
      : ''}
`;

const Description = styled.p`
  margin: 0;
  font-size: 12px;
  line-height: 18px;
  font-weight: ${Fonts.REGULAR};
  color: #607798;
  overflow: hidden;
  text-overflow: ellipsis;
  display: -webkit-box;
  -webkit-box-orient: vertical;
  word-break: break-word;

  ${({ $isTestSeries }) =>
    $isTestSeries
      ? `
    -webkit-line-clamp: 1;
    flex-shrink: 0;
    max-height: 18px;
  `
      : `
    -webkit-line-clamp: 2;
  `}
`;

const TagsRow = styled.div`
  display: flex;
  flex-shrink: 0;
  flex-wrap: wrap;
  align-items: center;
  gap: 8px;
  padding: 8px;
  width: 100%;

  ${({ $preventWrapOnMobile }) =>
    $preventWrapOnMobile
      ? `
    ${mediaQuery.uptoMobile} {
      flex-wrap: nowrap;
    }
  `
      : ''}
`;

const TypeBadge = styled.span`
  display: inline-flex;
  flex-shrink: 0;
  align-items: center;
  justify-content: center;
  height: 24px;
  padding: 0 8px;
  border-radius: 4px;
  background: #08bd80;
  font-size: 12px;
  line-height: 16px;
  font-weight: ${Fonts.SEMIBOLD};
  color: #ffffff;
  white-space: nowrap;
`;

const SubscriptionBadge = styled.span`
  display: inline-flex;
  flex-shrink: 0;
  align-items: center;
  gap: ${({ $isPrime }) => ($isPrime ? '2px' : '4px')};
  height: 24px;
  padding: ${({ $isPrime }) => ($isPrime ? '0 6px 1px' : '0 8px')};
  border: ${({ $borderColor, $isPrime }) =>
    $isPrime ? `0.923px solid ${$borderColor}` : `1px solid ${$borderColor}`};
  border-radius: ${({ $isPrime }) => ($isPrime ? '4.615px' : '4px')};
  font-size: 12px;
  line-height: ${({ $isPrime }) => ($isPrime ? '14.768px' : '16px')};
  font-weight: ${Fonts.SEMIBOLD};
  color: ${({ $textColor }) => $textColor};
  text-transform: ${({ $isPrime }) => ($isPrime ? 'capitalize' : 'none')};
  letter-spacing: ${({ $isPrime }) => ($isPrime ? '0.24px' : '0')};
`;

const MetaBadge = styled.span`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  min-width: 0;
  height: 24px;
  padding: 0 8px;
  border-radius: 4px;
  background: #f2f7fc;
  font-size: 12px;
  line-height: 16px;
  font-weight: ${Fonts.SEMIBOLD};
  color: #607798;
  white-space: nowrap;

  ${({ $grow }) => ($grow ? 'flex: 1 0 0;' : 'flex-shrink: 0;')}

  ${({ $equalFlexOnMobile }) =>
    $equalFlexOnMobile
      ? `
    ${mediaQuery.uptoMobile} {
      flex: 1 0 0;
      min-width: 0;
    }
  `
      : ''}
`;

const BadgeIcon = styled.img`
  flex-shrink: 0;
`;

const renderCategoryLabel = (categoryLabel, showLock) => {
  if (!showLock) {
    return <CategoryLabel>{categoryLabel}</CategoryLabel>;
  }

  return (
    <CategoryRow>
      <LockBadge aria-hidden>
        <LockIcon
          src={getImage('test-series-listing/cards/lock-icon.svg')}
          width={12}
          height={13.5}
          alt=""
        />
      </LockBadge>
      <CategoryLabel>{categoryLabel}</CategoryLabel>
    </CategoryRow>
  );
};

const TestSeriesListingTestCard = ({
  variant,
  categoryLabel,
  title,
  description,
  subscriptionType,
  showLock,
  questionCount,
  durationMinutes,
  testCount,
  dateRangeLabel,
  isLive,
  href,
  onClick
}) => {
  const subscriptionBadge = SUBSCRIPTION_BADGE_CONFIG[subscriptionType];
  const isClickable = Boolean(href || onClick);
  const isTestSeries = variant === TEST_LISTING_CARD_VARIANT.TEST_SERIES;

  const handleKeyDown = (event) => {
    if (!isClickable || !onClick) {
      return;
    }

    if (event.key === 'Enter' || event.key === ' ') {
      event.preventDefault();
      onClick();
    }
  };

  return (
    <Card
      $isLive={isLive}
      $isTestSeries={isTestSeries}
      $isClickable={isClickable}
      role={isClickable ? 'button' : undefined}
      tabIndex={isClickable ? 0 : undefined}
      onClick={onClick}
      onKeyDown={handleKeyDown}
    >
      <ContentBlock>
        {isLive ? (
          <HeaderGroup>
            <LiveRow>
              <LiveIndicatorIcon
                src={getImage(
                  'test-series-listing/cards/live-indicator-icon.svg'
                )}
                width={12}
                height={12}
                alt=""
              />
              <LivePill>LIVE</LivePill>
            </LiveRow>
            {renderCategoryLabel(categoryLabel, showLock)}
          </HeaderGroup>
        ) : (
          renderCategoryLabel(categoryLabel, showLock)
        )}
        <TextGroup $isLive={isLive} $isTestSeries={isTestSeries}>
          <Title $isTestSeries={isTestSeries}>{title}</Title>
          {description ? (
            <Description $isTestSeries={isTestSeries}>
              {description}
            </Description>
          ) : null}
        </TextGroup>
      </ContentBlock>
      <TagsRow $preventWrapOnMobile={!isTestSeries}>
        {isTestSeries ? (
          <>
            <TypeBadge>Test series</TypeBadge>
            <SubscriptionBadge
              $borderColor={subscriptionBadge.borderColor}
              $textColor={subscriptionBadge.textColor}
              $isPrime={subscriptionBadge.isPrime}
            >
              <BadgeIcon
                src={getImage(subscriptionBadge.icon)}
                width={subscriptionBadge.iconSize}
                height={subscriptionBadge.iconSize}
                alt=""
              />
              {subscriptionBadge.label}
            </SubscriptionBadge>
            <MetaBadge $grow>{testCount} tests</MetaBadge>
            {dateRangeLabel ? <MetaBadge>{dateRangeLabel}</MetaBadge> : null}
          </>
        ) : (
          <>
            <SubscriptionBadge
              $borderColor={subscriptionBadge.borderColor}
              $textColor={subscriptionBadge.textColor}
              $isPrime={subscriptionBadge.isPrime}
            >
              <BadgeIcon
                src={getImage(subscriptionBadge.icon)}
                width={subscriptionBadge.iconSize}
                height={subscriptionBadge.iconSize}
                alt=""
              />
              {subscriptionBadge.label}
            </SubscriptionBadge>
            {questionCount > 0 ? (
              <MetaBadge $grow $equalFlexOnMobile>
                {questionCount} questions
              </MetaBadge>
            ) : null}
            <MetaBadge $equalFlexOnMobile>{durationMinutes} min</MetaBadge>
          </>
        )}
      </TagsRow>
    </Card>
  );
};

export default TestSeriesListingTestCard;

TestSeriesListingTestCard.propTypes = {
  variant: PropTypes.oneOf(Object.values(TEST_LISTING_CARD_VARIANT)),
  categoryLabel: PropTypes.string.isRequired,
  title: PropTypes.string.isRequired,
  description: PropTypes.string,
  subscriptionType: PropTypes.oneOf(Object.values(TEST_SUBSCRIPTION_BADGE))
    .isRequired,
  showLock: PropTypes.bool,
  questionCount: PropTypes.number,
  durationMinutes: PropTypes.number,
  testCount: PropTypes.number,
  dateRangeLabel: PropTypes.string,
  isLive: PropTypes.bool,
  href: PropTypes.string,
  onClick: PropTypes.func
};

TestSeriesListingTestCard.defaultProps = {
  variant: TEST_LISTING_CARD_VARIANT.QUIZ,
  description: '',
  showLock: false,
  questionCount: 0,
  durationMinutes: 0,
  testCount: 0,
  dateRangeLabel: '',
  isLive: false,
  href: undefined,
  onClick: undefined
};
